/**
 * CableJunctionRenderer
 *
 * Renders a single cable junction box on the floor plan.
 * Uses the inspection-panel lucide icon with sizing behavior
 * similar to LV products (scales with floor plan, not zoom).
 */

import React, { useCallback } from 'react';
import type { CableJunction } from '@/types/cablePaths';

interface CableJunctionRendererProps {
  junction: CableJunction;
  isSelected: boolean;
  isHovered: boolean;
  isSnapTarget: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  /** Whether this junction is currently being dragged */
  isDragging?: boolean;
  /** Base scale from floor plan metadata (like LV products) */
  baseScale?: number;
}

// Base icon size (before applying baseScale) - similar to LV products
const ICON_BASE_SIZE = 24;

// Lucide inspection-panel icon SVG path
const INSPECTION_PANEL_PATH = "M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z";

export function CableJunctionRenderer({
  junction,
  isSelected,
  isHovered,
  isSnapTarget,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  isInteractive,
  isDragging = false,
  baseScale = 1.0,
}: CableJunctionRendererProps) {
  // Calculate size - use junction.scale * baseScale like LV products
  // baseScale is from floor plan dimensions, junction.scale is per-junction adjustment
  const effectiveScale = (junction.scale || 1.0) * baseScale;
  const iconSize = ICON_BASE_SIZE * effectiveScale;
  const halfSize = iconSize / 2;
  const labelOffset = iconSize + 8 * effectiveScale;
  const fontSize = 11 * effectiveScale;
  const strokeWidth = 2 * effectiveScale;

  // Colors based on state
  const getIconColor = () => {
    if (isDragging) return '#f59e0b'; // Orange when dragging
    if (isSnapTarget) return '#22c55e'; // Green when being snapped to
    if (isSelected) return '#3b82f6'; // Blue when selected
    if (isHovered) return '#60a5fa'; // Lighter blue when hovered
    return '#000000'; // Black default
  };

  const getBorderColor = () => {
    if (isDragging) return '#d97706';
    if (isSnapTarget) return '#16a34a';
    if (isSelected) return '#2563eb';
    if (isHovered) return '#3b82f6';
    return '#4b5563';
  };

  // Handle touch wrapper
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isInteractive) return;
      onTouchStart?.(e);
    },
    [isInteractive, onTouchStart]
  );

  return (
    <g
      data-junction-id={junction.id}
      style={{ cursor: isInteractive ? 'pointer' : 'default' }}
    >
      {/* Invisible larger hit area for easier selection */}
      <rect
        x={junction.x - halfSize * 1.5}
        y={junction.y - halfSize * 1.5}
        width={iconSize * 1.5}
        height={iconSize * 1.5}
        fill="transparent"
        stroke="none"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={onMouseDown}
        onTouchStart={handleTouchStart}
        pointerEvents={isInteractive ? 'all' : 'none'}
      />

      {/* Lucide inspection-panel icon - centered at junction point */}
      <g
        transform={`translate(${junction.x - halfSize}, ${junction.y - halfSize}) scale(${iconSize / 24})`}
        pointerEvents="none"
      >
        {/* Main rounded rectangle with white fill */}
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          fill="white"
          stroke={getIconColor()}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Four corner dots */}
        <path
          d="M7 7h.01M17 7h.01M7 17h.01M17 17h.01"
          fill="none"
          stroke={getIconColor()}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Selection ring when selected */}
      {isSelected && (
        <rect
          x={junction.x - halfSize - strokeWidth}
          y={junction.y - halfSize - strokeWidth}
          width={iconSize + strokeWidth * 2}
          height={iconSize + strokeWidth * 2}
          rx={4 * effectiveScale}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={strokeWidth}
          strokeDasharray={`${4 * effectiveScale} ${2 * effectiveScale}`}
          pointerEvents="none"
        />
      )}

      {/* Snap indicator ring when being targeted */}
      {isSnapTarget && (
        <rect
          x={junction.x - halfSize - strokeWidth * 2}
          y={junction.y - halfSize - strokeWidth * 2}
          width={iconSize + strokeWidth * 4}
          height={iconSize + strokeWidth * 4}
          rx={4 * effectiveScale}
          fill="none"
          stroke="#22c55e"
          strokeWidth={strokeWidth}
          strokeDasharray={`${4 * effectiveScale} ${4 * effectiveScale}`}
          pointerEvents="none"
        >
          <animate
            attributeName="stroke-opacity"
            values="1;0.5;1"
            dur="1s"
            repeatCount="indefinite"
          />
        </rect>
      )}

      {/* Junction name label */}
      <text
        x={junction.x}
        y={junction.y + labelOffset}
        textAnchor="middle"
        fill={isSelected ? '#3b82f6' : '#374151'}
        fontSize={fontSize}
        fontWeight={isSelected ? 600 : 500}
        fontFamily="system-ui, sans-serif"
        pointerEvents="none"
        style={{ userSelect: 'none' }}
      >
        {junction.name}
      </text>
    </g>
  );
}
